import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Card } from 'react-bootstrap';
import { getThemeColors } from '../../utils/chartTheme';

/* ------------------------------------------------------------------ */
/*  Hauptkomponente                                                     */
/* ------------------------------------------------------------------ */

/**
 * Jahresdiagramm fuer PV-Jahresvergleich.
 * Balkendiagramm mit Trendlinie (lineare Regression).
 * Umschaltbar zwischen Gesamtertrag / spezifischer Ertrag / Aenderung.
 *
 * @param {object} props
 * @param {Array}  props.jahreswerte — Array von YearlyYieldValue
 */
export default function JahresDiagramm({ jahreswerte }) {
    const [ansicht, setAnsicht] = useState('gesamtertrag');

    // Wenn keine Daten oder nur 1 Jahr: Hinweis anzeigen
    if (!jahreswerte || jahreswerte.length < 2) {
        return (
            <Card className="chart-card mb-3">
                <Card.Body>
                    <div className="text-muted text-center py-4">
                        <i className="bi bi-info-circle me-1" />
                        {' '}
                        Nicht genuegend Jahreswerte fuer ein Diagramm vorhanden.
                    </div>
                </Card.Body>
            </Card>
        );
    }

    var hatTrendLinie = hasTrendLine(jahreswerte);

    return (
        <Card className="chart-card mb-3">
            <Card.Body>
                {/* Ansicht-Umschalter */}
                <div className="d-flex justify-content-center gap-2 mb-3 flex-wrap">
                    <AnsichtButton
                        active={ansicht === 'gesamtertrag'}
                        onClick={() => setAnsicht('gesamtertrag')}
                        label="Gesamtertrag"
                        unit="kWh"
                    />
                    <AnsichtButton
                        active={ansicht === 'spezifischerErtrag'}
                        onClick={() => setAnsicht('spezifischerErtrag')}
                        label="Spez. Ertrag"
                        unit="kWh/kWp"
                    />
                    <AnsichtButton
                        active={ansicht === 'anderung'}
                        onClick={() => setAnsicht('anderung')}
                        label="Aenderung"
                        unit="%"
                    />
                </div>

                <Bar
                    data={buildChartData(jahreswerte, ansicht)}
                    options={buildChartOptions(jahreswerte, ansicht)}
                />

                {/* Eigene Legende */}
                <ChartLegende hatTrend={hatTrendLinie} />
            </Card.Body>
        </Card>
    );
}

/* ------------------------------------------------------------------ */
/*  Ansicht-Button                                                      */
/* ------------------------------------------------------------------ */

function AnsichtButton({ active, onClick, label, unit }) {
    return (
        <button
            className={active
                ? 'btn btn-sm btn-outline-primary active'
                : 'btn btn-sm btn-outline-secondary'}
            onClick={onClick}
            type="button"
        >
            {label} ({unit})
        </button>
    );
}

/* ------------------------------------------------------------------ */
/*  Legende                                                             */
/* ------------------------------------------------------------------ */

function ChartLegende({ hatTrend }) {
    return (
        <div className="mt-2 text-center small text-muted chart-legend">
            <span className="me-3">
                <FarbeBox color={FARBE_INCLUDED} /> Einschliesslich Trend
            </span>
            <span className="me-3">
                <FarbeBox color={FARBE_EXCLUDED} /> Ausgeschlossen
            </span>
            <span className="me-3">
                <FarbeBox color={FARBE_CURRENT} /> Aktuelles Jahr
            </span>
            {hatTrend && (
                <span>
                    <span style={{
                        display: 'inline-block',
                        width: 18,
                        height: 2,
                        backgroundColor: FARBE_TREND,
                        verticalAlign: 'middle',
                        marginRight: 4,
                    }} />
                    Trendlinie
                </span>
            )}
        </div>
    );
}

function FarbeBox({ color }) {
    return (
        <span style={{
            display: 'inline-block',
            width: 12,
            height: 16,
            backgroundColor: color,
            verticalAlign: 'middle',
            marginRight: 3,
            borderRadius: 2,
        }} />
    );
}

/* ------------------------------------------------------------------ */
/*  Farb-Konstanten                                                     */
/* ------------------------------------------------------------------ */

var FARBE_INCLUDED    = 'rgba(245, 158, 11, 0.75)';   // Solar-Ambra
var FARBE_EXCLUDED    = 'rgba(108, 117, 125, 0.20)';  // Grau transparent (leichter)
var FARBE_CURRENT     = 'rgba(13, 202, 240, 0.65)';   // Cyan – aktuelles Jahr hervorheben
var FARBE_TREND       = 'rgba(220, 53, 69, 0.85)';    // Rot Trendlinie
var FARBE_BORDER      = 'rgba(217, 119, 6, 0.50)';

/* ------------------------------------------------------------------ */
/*  Chart-Daten                                                         */
/* ------------------------------------------------------------------ */

function buildChartData(items, ansicht) {
    var labels = [];
    var barData = [];
    var backgroundColors = [];
    var borderColors = [];
    var pointRadii = [];

    for (var i = 0; i < items.length; i++) {
        var item = items[i];
        labels.push(String(item.jahr));
        barData.push(getYieldValue(item, ansicht));

        // Farbe je Status
        if (item.voelligkeit === 2) {
            backgroundColors.push(FARBE_CURRENT);
        } else if (!item.inTrendEnthalten) {
            backgroundColors.push(FARBE_EXCLUDED);
        } else {
            backgroundColors.push(FARBE_INCLUDED);
        }

        borderColors.push(
            item.voelligkeit === 2
                ? 'rgba(13, 202, 240, 0.80)'
                : item.inTrendEnthalten
                    ? FARBE_BORDER
                    : 'rgba(108, 117, 125, 0.30)'
        );
        pointRadii.push(item.inTrendEnthalten ? 3 : 0);
    }

    return {
        labels: labels,
        datasets: [
            // Trendlinie (line dataset im Bar-Chart)
            {
                type: 'line',
                label: 'Trendlinie',
                data: computeLinearRegression(items, ansicht),
                borderColor: FARBE_TREND,
                backgroundColor: FARBE_TREND,
                borderWidth: 2.5,
                pointRadius: pointRadii,
                pointHoverRadius: items.map(function () { return 6; }),
                pointBackgroundColor: FARBE_TREND,
                tension: 0,
                fill: false,
                order: 0,
            },
            // Balken
            {
                type: 'bar',
                label: getAnsichtLabel(ansicht),
                data: barData,
                backgroundColor: backgroundColors,
                borderColor: borderColors,
                borderWidth: 1,
                borderRadius: 3,
                barPercentage: 0.70,
                categoryPercentage: 0.80,
                order: 1,
            },
        ],
    };
}

/* ------------------------------------------------------------------ */
/*  Lineare Regression                                                  */
/* ------------------------------------------------------------------ */

function computeLinearRegression(items, ansicht) {
    var included = [];
    for (var i = 0; i < items.length; i++) {
        if (items[i].inTrendEnthalten) {
            included.push({
                jahr: items[i].jahr,
                wert: getYieldValue(items[i], ansicht),
            });
        }
    }

    if (included.length < 2) return null;

    var n = included.length;
    var sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;

    for (var i = 0; i < n; i++) {
        var x = included[i].jahr;
        var y = included[i].wert;
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumXX += x * x;
    }

    var slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    var intercept = (sumY - slope * sumX) / n;

    // Trendwert fuer jedes Jahr berechnen
    return items.map(function (item) {
        if (item.inTrendEnthalten) {
            return slope * item.jahr + intercept;
        }
        return null;
    });
}

function hasTrendLine(items) {
    var count = 0;
    for (var i = 0; i < items.length; i++) {
        if (items[i].inTrendEnthalten) count++;
    }
    return count >= 2;
}

/* ------------------------------------------------------------------ */
/*  Chart-Optionen                                                      */
/* ------------------------------------------------------------------ */

function buildChartOptions(items, ansicht) {
    var tc = getThemeColors();

    return {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.0,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            // Datalabels global registriert — hier explizit deaktivieren
            datalabels: { display: false },
            tooltip: {
                backgroundColor: 'rgba(33, 37, 41, 0.92)',
                titleColor: '#e4e6ea',
                bodyColor: '#e4e6ea',
                padding: 10,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    title: function (tooltipItems) {
                        return 'Jahr ' + tooltipItems[0].label;
                    },
                    label: function (context) {
                        // Trendlinie
                        if (context.dataset.type === 'line') {
                            var val = context.raw;
                            if (val === null) return '';
                            return formatValue(val, ansicht) + '  Trend';
                        }

                        // Balkenwert
                        var wert = context.raw;
                        return formatValue(wert, ansicht) + ' ' + getAnsichtLabel(ansicht);
                    },
                    afterBody: function (tooltipItems) {
                        if (!tooltipItems || tooltipItems.length === 0) return [];
                        var idx = tooltipItems[0].dataIndex;
                        var jahrItem = items[idx];
                        if (!jahrItem) return [];

                        var lines = [];
                        lines.push(
                            'Vollstaendigkeit: '
                            + Math.round(jahrItem.vollstaendigkeitProzent || 0)
                            + '%'
                        );

                        if (jahrItem.inTrendEnthalten) {
                            lines.push('Im Trend enthalten: Ja');
                        } else if (jahrItem.ausschlussgrund) {
                            lines.push('Ausschluss: ' + jahrItem.ausschlussgrund);
                        }

                        return lines;
                    },
                },
            },
            legend: { display: false },
        },
        scales: {
            x: {
                grid: { color: tc.gridColor, drawBorder: false },
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    color: tc.textColor,
                },
                title: {
                    display: true,
                    text: 'Jahr',
                    color: tc.textColor,
                },
            },
            y: {
                grid: { color: tc.gridColor, drawBorder: false },
                beginAtZero: ansicht !== 'anderung',
                ticks: {
                    color: tc.textColor,
                    callback: function (v) {
                        return formatValue(v, ansicht);
                    },
                },
                title: {
                    display: true,
                    text: getAnsichtEinheit(ansicht),
                    color: tc.textColor,
                },
            },
        },
    };
}

/* ------------------------------------------------------------------ */
/*  Hilfsfunktionen                                                     */
/* ------------------------------------------------------------------ */

function getYieldValue(item, ansicht) {
    switch (ansicht) {
        case 'spezifischerErtrag':
            return item.spezifischerErtragKWhPerKwp || 0;
        case 'anderung':
            return item.abweichungRelativProzent != null
                ? item.abweichungRelativProzent
                : 0;
        default: // gesamtertrag
            return item.gesamtertragKWh || 0;
    }
}

function getAnsichtLabel(ansicht) {
    switch (ansicht) {
        case 'spezifischerErtrag': return 'spez. Ertrag';
        case 'anderung':          return 'Aenderung';
        default:                 return 'Gesamtertrag';
    }
}

function getAnsichtEinheit(ansicht) {
    switch (ansicht) {
        case 'spezifischerErtrag': return 'kWh/kWp';
        case 'anderung':          return '%';
        default:                 return 'kWh';
    }
}

function formatValue(val, ansicht) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    var num = Number(val);
    var decimals = ansicht === 'anderung' ? 1 : 0;
    try {
        return num.toLocaleString('de-DE', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    } catch (_) {
        return String(num);
    }
}
